import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { register, checkUsernameExists, checkEmailExists, checkPhoneExists } from '../../api/auth';
import WindowControls from '../../components/WindowControls';
import DropdownMenu, { DropdownMenuItem } from '../../components/DropdownMenu';
import { APP_LANGUAGE_OPTIONS } from '../../i18n/languageOptions';
import { getLanguageByIP } from '../../utils/geoLanguage';
import './SignUp.css';

// 检查是否在Electron环境中
const isElectron = typeof window !== 'undefined' && window.require && window.require('electron');

// 用户与隐私协议链接，待定后可在此填写完整 URL
const AGREEMENT_URL = '';

// 防抖函数
const debounce = <T extends (...args: any[]) => any>(func: T, wait: number): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

type ValidationStatus = 'normal' | 'warning' | 'error';

// 根据浏览器语言推断默认国家代码
const getDefaultCountryCode = (): string => {
  const lang = navigator.language || navigator.languages?.[0] || 'zh-CN';
  
  if (lang.startsWith('zh-CN')) return '+86';
  if (lang.startsWith('zh-HK')) return '+852';
  if (lang.startsWith('zh-TW')) return '+886';
  if (lang.startsWith('ja')) return '+81';
  if (lang.startsWith('ko')) return '+82';
  if (lang.startsWith('en-US') || lang.startsWith('en-CA')) return '+1';
  if (lang.startsWith('en-GB')) return '+44';
  if (lang.startsWith('fr')) return '+33';
  if (lang.startsWith('de')) return '+49';
  if (lang.startsWith('en-AU')) return '+61';
  
  return '+86'; // 默认中国
};

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const languageButtonRef = useRef<HTMLDivElement>(null);
  const [languageMenuVisible, setLanguageMenuVisible] = useState(false);
  const [languageMenuPosition, setLanguageMenuPosition] = useState({ top: 0, left: 0 });

  const getLanguageName = (code: string): string => {
    const nameMap: Record<string, string> = {
      'zh-CN': '中文简体', 'zh-HK': '中文繁体', 'en': 'English'
    };
    return nameMap[code] || nameMap['zh-CN'] || '中文简体';
  };
  const languageOptions = APP_LANGUAGE_OPTIONS;
  const rawLang = i18n.language || 'zh-CN';
  const currentLanguage = rawLang.startsWith('zh-HK') ? 'zh-HK' : rawLang.startsWith('zh') ? 'zh-CN' : 'en';

  useEffect(() => {
    if (languageMenuVisible && languageButtonRef.current) {
      const rect = languageButtonRef.current.getBoundingClientRect();
      setLanguageMenuPosition({ top: rect.bottom + 4, left: rect.left });
    }
  }, [languageMenuVisible]);

  // 表单字段
  const [username, setUsername] = useState('');
  const [nickname, setNickname] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [role, setRole] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [email, setEmail] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState(getDefaultCountryCode());
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  
  // 验证状态
  const [usernameStatus, setUsernameStatus] = useState<ValidationStatus>('normal');
  const [nicknameStatus, setNicknameStatus] = useState<ValidationStatus>('normal');
  const [roleStatus, setRoleStatus] = useState<ValidationStatus>('normal');
  const [countryStatus, setCountryStatus] = useState<ValidationStatus>('normal');
  const [emailStatus, setEmailStatus] = useState<ValidationStatus>('normal');
  const [phoneStatus, setPhoneStatus] = useState<ValidationStatus>('normal');
  const [passwordStatus, setPasswordStatus] = useState<ValidationStatus>('normal');
  const [confirmPasswordStatus, setConfirmPasswordStatus] = useState<ValidationStatus>('normal');
  
  // UI 状态
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // 创建 ref 用于用户名输入框
  const usernameInputRef = useRef<HTMLInputElement>(null);
  
  // 职业角色下拉菜单相关状态
  const roleDropdownRef = useRef<HTMLDivElement>(null);
  const [roleDropdownVisible, setRoleDropdownVisible] = useState(false);
  
  // 职业角色选项（暂时隐藏职业角色/国家/城市时保留供恢复使用）
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const roleOptions: DropdownMenuItem[] = [
    { id: '制片人', label: '制片人' },
    { id: '监制', label: '监制' },
    { id: '导演', label: '导演' },
    { id: '编剧', label: '编剧' },
    { id: '副导演', label: '副导演' },
    { id: '统筹', label: '统筹' },
    { id: '影视投资人', label: '影视投资人' },
    { id: '影视爱好者', label: '影视爱好者' },
    { id: '学生', label: '学生' },
    { id: '其他从业者', label: '其他从业者' },
  ];
  
  // 组件挂载时自动聚焦到用户名输入框
  useEffect(() => {
    usernameInputRef.current?.focus();
  }, []);

  // 无本地语言缓存时，根据 IP 推断默认语言
  useEffect(() => {
    const cached = localStorage.getItem('i18nextLng');
    if (cached) return;
    getLanguageByIP().then((lang) => {
      i18n.changeLanguage(lang);
    });
  }, [i18n]);
  
  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target as Node)) {
        setRoleDropdownVisible(false);
      }
    };
    
    if (roleDropdownVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [roleDropdownVisible]);

  // 用户名验证：长度 1～50，仅限英文、数字及常用符号（与注册提示一致）
  const validateUsername = (value: string): boolean => {
    return (
      value.length > 0 &&
      value.length <= 50 &&
      /^[a-zA-Z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+$/.test(value)
    );
  };

  // 昵称验证：不超过16个字符
  const validateNickname = (value: string): boolean => {
    return value.length <= 16;
  };

  // 邮箱验证
  const validateEmail = (value: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  // 手机号验证（根据选择的国际区号验证）
  const validatePhone = (value: string, countryCode: string): boolean => {
    if (!value) return false;
    
    // 移除空格和连字符
    const cleaned = value.replace(/[\s-]/g, '');
    
    // 根据国家代码验证
    switch (countryCode) {
      case '+86': // 中国：11位数字，1开头
        return /^1[3-9]\d{9}$/.test(cleaned);
      case '+852': // 香港：8位数字
        return /^\d{8}$/.test(cleaned);
      case '+886': // 台湾：9位数字
        return /^\d{9}$/.test(cleaned);
      case '+81': // 日本：10-11位数字
        return /^\d{10,11}$/.test(cleaned);
      case '+82': // 韩国：9-10位数字
        return /^\d{9,10}$/.test(cleaned);
      case '+1': // 美国/加拿大：10位数字
        return /^\d{10}$/.test(cleaned);
      case '+44': // 英国：10-11位数字
        return /^\d{10,11}$/.test(cleaned);
      case '+33': // 法国：9位数字
        return /^\d{9}$/.test(cleaned);
      case '+49': // 德国：10-11位数字
        return /^\d{10,11}$/.test(cleaned);
      case '+61': // 澳大利亚：9位数字
        return /^\d{9}$/.test(cleaned);
      default:
        return /^\d{6,15}$/.test(cleaned); // 通用验证：6-15位数字
    }
  };

  // 密码验证：不少于6位，仅限大小写英文字母、数字、常用英文符号
  const validatePassword = (value: string): boolean => {
    if (value.length < 6) return false;
    return /^[a-zA-Z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+$/.test(value);
  };

  // 防抖检查用户名重复
  const debouncedCheckUsername = useRef(
    debounce(async (value: string) => {
      if (!value) {
        setUsernameStatus('normal');
        return;
      }
      
      if (!validateUsername(value)) {
        setUsernameStatus('error');
        return;
      }
      
      const exists = await checkUsernameExists(value);
      setUsernameStatus(exists ? 'warning' : 'normal');
    }, 500)
  ).current;

  // 防抖检查邮箱重复
  const debouncedCheckEmail = useRef(
    debounce(async (value: string) => {
      if (!value) {
        setEmailStatus('normal');
        return;
      }
      
      if (!validateEmail(value)) {
        setEmailStatus('error');
        return;
      }
      
      const exists = await checkEmailExists(value);
      setEmailStatus(exists ? 'warning' : 'normal');
    }, 500)
  ).current;

  // 防抖检查手机号重复
  const debouncedCheckPhone = useRef(
    debounce(async (value: string, countryCode: string) => {
      if (!value) {
        setPhoneStatus('normal');
        return;
      }
      
      if (!validatePhone(value, countryCode)) {
        setPhoneStatus('error');
        return;
      }
      
      // 分别检查国际区号和手机号
      const exists = await checkPhoneExists(value, countryCode);
      setPhoneStatus(exists ? 'warning' : 'normal');
    }, 500)
  ).current;

  // 处理用户名输入
  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);
    
    if (!value) {
      setUsernameStatus('normal');
      return;
    }
    
    if (!validateUsername(value)) {
      setUsernameStatus('error');
    } else {
      debouncedCheckUsername(value);
    }
  };

  // 处理昵称输入
  const handleNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNickname(value);
    
    if (!value) {
      setNicknameStatus('normal');
      return;
    }
    
    if (!validateNickname(value)) {
      setNicknameStatus('error');
    } else {
      setNicknameStatus('normal');
    }
  };

  // 处理邮箱输入
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    
    if (!value) {
      setEmailStatus('normal');
      return;
    }
    
    if (!validateEmail(value)) {
      setEmailStatus('error');
    } else {
      debouncedCheckEmail(value);
    }
    // 填写了邮箱时，若手机号为空则清除其错误状态（至少填一个即可）
    if (value && !phone) {
      setPhoneStatus('normal');
    }
  };

  // 处理手机号输入
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // 只允许数字
    setPhone(value);
    
    if (!value) {
      setPhoneStatus('normal');
      return;
    }
    
    if (!validatePhone(value, phoneCountryCode)) {
      setPhoneStatus('error');
    } else {
      debouncedCheckPhone(value, phoneCountryCode);
    }
    // 填写了手机号时，若邮箱为空则清除其错误状态（至少填一个即可）
    if (value && !email) {
      setEmailStatus('normal');
    }
  };

  // 处理职业角色选择（暂时隐藏职业角色/国家/城市时保留供恢复使用）
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRoleSelect = (item: DropdownMenuItem) => {
    setRole(item.label);
    setRoleDropdownVisible(false);
    // 如果之前有错误状态，选择后清除错误状态
    if (roleStatus === 'error') {
      setRoleStatus('normal');
    }
  };
  
  // 处理职业角色按钮点击（暂时隐藏职业角色/国家/城市时保留供恢复使用）
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRoleButtonClick = () => {
    setRoleDropdownVisible(!roleDropdownVisible);
  };

  // 处理国家输入（暂时隐藏职业角色/国家/城市时保留供恢复使用）
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCountryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCountry(value);
    
    // 如果之前有错误状态，输入后清除错误状态
    if (value && countryStatus === 'error') {
      setCountryStatus('normal');
    }
  };

  // 处理城市输入（暂时隐藏职业角色/国家/城市时保留供恢复使用）
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCity(e.target.value);
  };

  // 处理密码输入
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    
    if (!value) {
      setPasswordStatus('normal');
      return;
    }
    
    if (!validatePassword(value)) {
      setPasswordStatus('error');
    } else {
      setPasswordStatus('normal');
    }
    
    // 如果确认密码已输入，重新检查
    if (confirmPassword) {
      if (value !== confirmPassword) {
        setConfirmPasswordStatus('warning');
      } else {
        setConfirmPasswordStatus('normal');
      }
    }
  };

  // 处理确认密码输入
  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmPassword(value);
    
    if (!value) {
      setConfirmPasswordStatus('normal');
      return;
    }
    
    if (value !== password) {
      setConfirmPasswordStatus('warning');
    } else {
      setConfirmPasswordStatus('normal');
    }
  };

  // 处理协议链接点击
  const handleAgreementLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!AGREEMENT_URL) return;
    if (isElectron) {
      try {
        const { shell } = window.require('electron');
        shell.openExternal(AGREEMENT_URL);
      } catch (err) {
        console.warn('Failed to open agreement URL:', err);
      }
    } else {
      window.open(AGREEMENT_URL, '_blank', 'noopener,noreferrer');
    }
  };

  // 处理注册提交
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // 设置必填字段的错误状态（仅在点击注册按钮时触发）
    // 职业角色、国家已暂时隐藏，不再检查必填

    // 表单验证：邮箱和手机号至少填写一个
    const hasEmail = !!email.trim();
    const hasPhone = !!phone.trim();
    if (!hasEmail && !hasPhone) {
      setEmailStatus('error');
      setPhoneStatus('error');
      setError(t('signUp.atLeastOneContact'));
      return;
    }

    if (!username || !nickname || !password || !confirmPassword) {
      setError(t('signUp.fillAllRequired'));
      return;
    }

    if (!agreeToTerms) {
      setError(t('signUp.agreeToAgreement'));
      return;
    }

    // 验证所有字段
    if (!validateUsername(username)) {
      setError(t('signUp.usernameFormat'));
      return;
    }

    if (!validateNickname(nickname)) {
      setError(t('signUp.nicknameTooLong'));
      return;
    }

    // 若填写了邮箱则验证格式
    if (hasEmail && !validateEmail(email)) {
      setError(t('signUp.emailInvalid'));
      return;
    }

    // 若填写了手机号则验证格式
    if (hasPhone && !validatePhone(phone, phoneCountryCode)) {
      setError(t('signUp.phoneInvalid'));
      return;
    }

    if (!validatePassword(password)) {
      setError(t('signUp.passwordInvalid'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('signUp.passwordMismatch'));
      return;
    }

    // 检查是否有警告状态（重复）
    if (usernameStatus === 'warning' || emailStatus === 'warning' || phoneStatus === 'warning') {
      setError(t('signUp.usernameOrContactTaken'));
      return;
    }

    setLoading(true);

    try {
      const response = await register({
        userName: username,
        password,
        email: hasEmail ? email : undefined,
        phoneCountryCode: hasPhone ? phoneCountryCode : undefined,
        phone: hasPhone ? phone : undefined,
        nickName: nickname,
        inviteCode: inviteCode.trim() || undefined,
        role: role || undefined,
        country: country || undefined,
        city: city || undefined,
        language: i18n.language,
      });

      console.log('Register response:', response);
      setSuccess(true);
      setError('');
    } catch (error: any) {
      console.error('Register error:', error);
      
      let errorMessage = t('signUp.registerFailed');
      if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = error.response.data?.message || t('signUp.registerInfoError');
            break;
          case 500:
            errorMessage = t('signUp.serverError');
            break;
          default:
            errorMessage = error.response.data?.message || t('signUp.registerFailed');
        }
      } else if (error.request) {
        errorMessage = t('signUp.networkError');
      }
      
      setError(errorMessage);
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  // 获取输入框的 className
  const getInputClassName = (status: ValidationStatus): string => {
    if (status === 'warning') return 'warning';
    if (status === 'error') return 'error';
    return '';
  };

  // 获取每行的消息类型（'warning' | 'error' | '')
  const getRowMessageType = (rowIndex: number): 'warning' | 'error' | '' => {
    switch (rowIndex) {
      case 0: // 第一行：用户名和昵称
        if (usernameStatus === 'warning' || nicknameStatus === 'warning') return 'warning';
        if (usernameStatus === 'error' || nicknameStatus === 'error') return 'error';
        return '';
      case 1: // 第二行：邮箱和手机号（至少填一个）
        if (emailStatus === 'warning' || phoneStatus === 'warning') return 'warning';
        if (emailStatus === 'error' || phoneStatus === 'error') return 'error';
        return '';
      case 2: // 第三行：密码和确认密码
        if (passwordStatus === 'error') return 'error';
        if (confirmPasswordStatus === 'warning') return 'warning';
        return '';
      default:
        return '';
    }
  };

  // 获取每行的警告/错误消息
  const getRowWarningMessage = (rowIndex: number): string => {
    switch (rowIndex) {
      case 0: // 第一行：用户名和昵称
        if (usernameStatus === 'warning') return t('signUp.usernameTaken');
        if (usernameStatus === 'error') return t('signUp.usernameFormat');
        if (nicknameStatus === 'error') return t('signUp.nicknameTooLong');
        return '';
      case 1: // 第二行：邮箱和手机号（至少填一个）
        if ((!email.trim() && !phone.trim()) && (emailStatus === 'error' || phoneStatus === 'error')) {
          return t('signUp.atLeastOneContact');
        }
        if (emailStatus === 'warning') return t('signUp.emailTaken');
        if (emailStatus === 'error') return t('signUp.emailInvalid');
        if (phoneStatus === 'warning') return t('signUp.phoneTaken');
        if (phoneStatus === 'error') {
          return phoneCountryCode === '+86' ? t('signUp.phoneInvalidCN') : t('signUp.phoneInvalid');
        }
        return '';
      case 2: // 第三行：密码和确认密码
        if (passwordStatus === 'error') return t('signUp.passwordInvalid');
        if (confirmPasswordStatus === 'warning') return t('signUp.passwordMismatch');
        return '';
      default:
        return '';
    }
  };

  if (success) {
    return (
      <div className="signup-container">
        <WindowControls />
        <div className="signup-content">
          <div className="signup-logo">
            <img src={require('../../assets/images/logo_horizontal.png')} alt="LINEUP" />
          </div>
          <div className="signup-box signup-success-box">
            <div
              ref={languageButtonRef}
              className="signup-lang-btn"
              onClick={() => setLanguageMenuVisible(!languageMenuVisible)}
              title={getLanguageName(currentLanguage)}
            >
              <span className="signup-lang-name">{getLanguageName(currentLanguage)}</span>
            </div>
            <DropdownMenu
              items={languageOptions}
              visible={languageMenuVisible}
              onClose={() => setLanguageMenuVisible(false)}
              onSelect={(item) => {
                i18n.changeLanguage(item.id as string);
                setLanguageMenuVisible(false);
              }}
              position={languageMenuPosition}
              useFixedPosition={true}
              selectedValue={currentLanguage}
              width={languageButtonRef.current?.offsetWidth}
            />
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <h2 style={{ color: 'var(--color-main)', marginBottom: '20px' }}>{t('signUp.successTitle')}</h2>
              <button 
                type="button" 
                className="signup-button signup-button-center" 
                onClick={() => navigate('/login')}
                style={{ marginTop: '20px' }}
              >
                {t('signUp.backToLogin')}
              </button>
            </div>
          </div>
        </div>
        <div className="signup-footer">
          <button type="button" className="signup-footer-link">{t('auth.aboutLineup')}</button>
          <span className="signup-footer-sep">|</span>
          <button type="button" className="signup-footer-link">{t('auth.userAgreement')}</button>
          <span className="signup-footer-sep">|</span>
          <button type="button" className="signup-footer-link">{t('auth.privacyPolicy')}</button>
          <span className="signup-footer-sep">|</span>
          <button type="button" className="signup-footer-link">{t('auth.softwareUpdate')}</button>
          <span className="signup-footer-sep">|</span>
          <button type="button" className="signup-footer-link">{t('auth.help')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-container">
      <WindowControls />
      <div className="signup-content">
        <div className="signup-logo">
          <img src={require('../../assets/images/logo_horizontal.png')} alt="LINEUP" />
        </div>
        <div className="signup-box">
          <div
            ref={languageButtonRef}
            className="signup-lang-btn"
            onClick={() => setLanguageMenuVisible(!languageMenuVisible)}
            title={getLanguageName(currentLanguage)}
          >
            <span className="signup-lang-name">{getLanguageName(currentLanguage)}</span>
          </div>
          <DropdownMenu
            items={languageOptions}
            visible={languageMenuVisible}
            onClose={() => setLanguageMenuVisible(false)}
            onSelect={(item) => {
              i18n.changeLanguage(item.id as string);
              setLanguageMenuVisible(false);
            }}
            position={languageMenuPosition}
            useFixedPosition={true}
            selectedValue={currentLanguage}
            width={languageButtonRef.current?.offsetWidth}
          />
          <form onSubmit={handleRegister}>
            <h3 className="signup-form-section-title">{t('signUp.basicInfo')}</h3>
            <div className="signup-input-row">
              <div className="signup-input-group">
                <input
                  type="text"
                  placeholder={t('signUp.usernamePlaceholder')}
                  value={username}
                  onChange={handleUsernameChange}
                  disabled={loading}
                  ref={usernameInputRef}
                  maxLength={50}
                  className={getInputClassName(usernameStatus)}
                />
              </div>
              <div className="signup-input-group">
                <input
                  type="text"
                  placeholder={t('signUp.nicknamePlaceholder')}
                  value={nickname}
                  onChange={handleNicknameChange}
                  disabled={loading}
                  className={getInputClassName(nicknameStatus)}
                />
              </div>
              <div className="signup-input-group signup-invite-code-group">
                <input
                  type="text"
                  placeholder={t('signUp.inviteCodePlaceholder')}
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  disabled={loading}
                  className="signup-invite-code-input"
                />
              </div>
            </div>
            <div className="signup-row-hint-container">
              {getRowWarningMessage(0) ? (
                <span className={getRowMessageType(0) === 'error' ? 'signup-row-error' : 'signup-row-warning'}>
                  {getRowWarningMessage(0)}
                </span>
              ) : (
                <span className="signup-row-hint">{t('signUp.usernameHint')}</span>
              )}
            </div>
            {/* 暂时隐藏：职业角色、国家、城市 */}
            {/* <div className="signup-input-row">
              <div className="signup-input-group" ref={roleDropdownRef} style={{ position: 'relative' }}>
                <div
                  className={`signup-role-dropdown-button ${getInputClassName(roleStatus)}`}
                  onClick={loading ? undefined : handleRoleButtonClick}
                  style={{ 
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                    color: role ? 'var(--color-gray1)' : 'var(--color-gray4)'
                  }}
                >
                  {role || '职业角色'}
                </div>
                {roleDropdownVisible && !loading && (
                  <DropdownMenu
                    items={roleOptions}
                    visible={roleDropdownVisible}
                    onClose={() => setRoleDropdownVisible(false)}
                    onSelect={handleRoleSelect}
                    position={{
                      top: 'calc(100% + 2px)',
                      left: 0,
                    }}
                    width="100%"
                    selectedValue={role}
                  />
                )}
              </div>
              <div className="signup-input-group">
                <input
                  type="text"
                  placeholder="国家"
                  value={country}
                  onChange={handleCountryChange}
                  disabled={loading}
                  className={getInputClassName(countryStatus)}
                />
              </div>
              <div className="signup-input-group">
                <input
                  type="text"
                  placeholder="城市（选填）"
                  value={city}
                  onChange={handleCityChange}
                  disabled={loading}
                />
              </div>
            </div>
            <div className="signup-row-hint-container">
              {getRowWarningMessage(1) ? (
                <span className={getRowMessageType(1) === 'error' ? 'signup-row-error' : 'signup-row-warning'}>
                  {getRowWarningMessage(1)}
                </span>
              ) : (
                <span className="signup-row-hint">请务必填写你真实的国家、城市信息，以便于LINEUP为你提供更好的服务</span>
              )}
            </div> */}
            <h3 className="signup-form-section-title">{t('signUp.contactInfo')}</h3>
            <div className="signup-input-row">
              <div className="signup-input-group">
                <input
                  type="email"
                  placeholder={t('signUp.emailPlaceholder')}
                  value={email}
                  onChange={handleEmailChange}
                  disabled={loading}
                  className={getInputClassName(emailStatus)}
                />
              </div>
              <div className="signup-input-group signup-phone-input-group">
                <div className="signup-phone-country-code-wrapper">
                  <span className="signup-phone-country-code-prefix">+</span>
                  <input
                    type="text"
                    placeholder="86"
                    value={phoneCountryCode.replace('+', '')}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value) {
                        setPhoneCountryCode(`+${value}`);
                      } else {
                        setPhoneCountryCode('+86');
                      }
                      // 重新验证手机号
                      if (phone) {
                        const newCode = value ? `+${value}` : '+86';
                        if (!validatePhone(phone, newCode)) {
                          setPhoneStatus('error');
                        } else {
                          debouncedCheckPhone(phone, newCode);
                        }
                      }
                    }}
                    disabled={loading}
                    className="signup-phone-country-code-input"
                  />
                </div>
                <input
                  type="tel"
                  placeholder={t('signUp.phonePlaceholder')}
                  value={phone}
                  onChange={handlePhoneChange}
                  disabled={loading}
                  className={`signup-phone-number-input ${getInputClassName(phoneStatus)}`}
                />
              </div>
            </div>
            <div className="signup-row-hint-container">
              {getRowWarningMessage(1) ? (
                <span className={getRowMessageType(1) === 'error' ? 'signup-row-error' : 'signup-row-warning'}>
                  {getRowWarningMessage(1)}
                </span>
              ) : (
                <span className="signup-row-hint">{t('signUp.contactHint')}</span>
              )}
            </div>
            <h3 className="signup-form-section-title">{t('signUp.passwordSection')}</h3>
            <div className="signup-input-row">
              <div className="signup-input-group">
                <input
                  type="password"
                  placeholder={t('signUp.passwordPlaceholder')}
                  value={password}
                  onChange={handlePasswordChange}
                  disabled={loading}
                  autoComplete="new-password"
                  className={getInputClassName(passwordStatus)}
                />
              </div>
              <div className="signup-input-group">
                <input
                  type="password"
                  placeholder={t('signUp.repeatPasswordPlaceholder')}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  disabled={loading}
                  autoComplete="new-password"
                  className={getInputClassName(confirmPasswordStatus)}
                />
              </div>
            </div>
            <div className="signup-row-hint-container">
              {getRowWarningMessage(2) ? (
                <span className={getRowMessageType(2) === 'error' ? 'signup-row-error' : 'signup-row-warning'}>
                  {getRowWarningMessage(2)}
                </span>
              ) : (
                <span className="signup-row-hint">{t('signUp.passwordHint')}</span>
              )}
            </div>
            <div className="signup-error-message-container">{error || ''}</div>
            <div className="signup-button-row">
              <button type="submit" className="signup-button" disabled={loading}>
                {loading ? t('signUp.registering') : t('signUp.register')}
              </button>
              <button 
                type="button" 
                className="signup-button signup-button-secondary" 
                onClick={() => navigate('/login')}
                disabled={loading}
              >
                {t('signUp.back')}
              </button>
            </div>
            <div className="signup-agreement-row">
              <label className="signup-checkbox-label">
                <input
                  type="checkbox"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  disabled={loading}
                  className="signup-checkbox"
                />
                <span>{t('signUp.agreementPrefix')}<a href={AGREEMENT_URL || '#'} className="signup-agreement-link" target="_blank" rel="noopener noreferrer" onClick={handleAgreementLinkClick}>{t('signUp.agreementLinkText')}</a>{t('signUp.agreementSuffix')}</span>
              </label>
              <p className="signup-agreement-hint">{t('signUp.agreementTip')}</p>
            </div>
          </form>
        </div>
      </div>
      <div className="signup-footer">
        <button type="button" className="signup-footer-link">{t('auth.aboutLineup')}</button>
        <span className="signup-footer-sep">|</span>
        <button type="button" className="signup-footer-link">{t('auth.userAgreement')}</button>
        <span className="signup-footer-sep">|</span>
        <button type="button" className="signup-footer-link">{t('auth.privacyPolicy')}</button>
        <span className="signup-footer-sep">|</span>
        <button type="button" className="signup-footer-link">{t('auth.softwareUpdate')}</button>
        <span className="signup-footer-sep">|</span>
        <button type="button" className="signup-footer-link">{t('auth.help')}</button>
      </div>
    </div>
  );
};

export default SignUp;
